import type { PackageResponse } from "./packagesApi";

export type MoveDirection = "up" | "down";

function getSiblingKey(pkg: PackageResponse): string {
  if (pkg.parentPackageId) {
    return `parent:${pkg.parentPackageId}`;
  }

  return `region:${pkg.regionId ?? "NO_REGION"}`;
}

function getSiblingIndexes(packages: readonly PackageResponse[], packageId: string): number[] {
  const pkg = packages.find((p) => p.id.id === packageId);

  if (!pkg) {
    return [];
  }

  const siblingKey = getSiblingKey(pkg);

  return packages.reduce<number[]>((indexes, current, index) => {
    if (getSiblingKey(current) === siblingKey) {
      indexes.push(index);
    }

    return indexes;
  }, []);
}

function updateSiblingOrders(
  packages: readonly PackageResponse[],
  siblingIndexes: readonly number[],
): PackageResponse[] {
  const result = [...packages];

  siblingIndexes.forEach((packageIndex, order) => {
    result[packageIndex] = {
      ...result[packageIndex],
      order,
    };
  });

  return result;
}

export function movePackage(
  packages: readonly PackageResponse[],
  packageId: string,
  direction: MoveDirection,
): PackageResponse[] {
  const siblingIndexes = getSiblingIndexes(packages, packageId);

  if (siblingIndexes.length === 0) {
    return [...packages];
  }

  const packageIndex = packages.findIndex((pkg) => pkg.id.id === packageId);
  const siblingPosition = siblingIndexes.indexOf(packageIndex);

  if (siblingPosition === -1) {
    return [...packages];
  }

  const targetSiblingPosition = direction === "up" ? siblingPosition - 1 : siblingPosition + 1;

  if (targetSiblingPosition < 0 || targetSiblingPosition >= siblingIndexes.length) {
    return [...packages];
  }

  const targetIndex = siblingIndexes[targetSiblingPosition];

  const result = [...packages];

  [result[packageIndex], result[targetIndex]] = [result[targetIndex], result[packageIndex]];

  return updateSiblingOrders(result, siblingIndexes);
}
